import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type PlanType = 'free' | 'pro' | 'premium';

export interface SubscriptionStatus {
  subscribed: boolean;
  plan_type: PlanType;
  product_id: string | null;
  subscription_end: string | null;
}

export interface PlanFeatures {
  creditsPerMonth: number;
  maxResumes: number;
  maxLetters: number;
  allTemplates: boolean;
  atsScoreChecker: boolean;
  linkedinOptimizer: boolean;
  jobAnalyzer: boolean;
  jobSearch: boolean;
  smartApply: boolean;
  companyInsights: boolean;
  aiApplication: boolean;
  aiInterviewPrep: boolean;
  salaryNegotiation: boolean;
  keywordMatching: boolean;
  versionHistory: boolean;
  analytics: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
}

const PLAN_FEATURES: Record<PlanType, PlanFeatures> = {
  free: {
    creditsPerMonth: 3,
    maxResumes: 1,
    maxLetters: 1,
    allTemplates: false,
    atsScoreChecker: false,
    linkedinOptimizer: false,
    jobAnalyzer: false,
    jobSearch: false,
    smartApply: false,
    companyInsights: false,
    aiApplication: false,
    aiInterviewPrep: false,
    salaryNegotiation: false,
    keywordMatching: false,
    versionHistory: false,
    analytics: false,
    customBranding: false,
    prioritySupport: false,
  },
  pro: {
    creditsPerMonth: 25,
    maxResumes: 5,
    maxLetters: 5,
    allTemplates: true,
    atsScoreChecker: true,
    linkedinOptimizer: true,
    jobAnalyzer: true,
    jobSearch: false,
    smartApply: true,
    companyInsights: false,
    aiApplication: false,
    aiInterviewPrep: false,
    salaryNegotiation: true,
    keywordMatching: true,
    versionHistory: true,
    analytics: false,
    customBranding: false,
    prioritySupport: true,
  },
  premium: {
    creditsPerMonth: 100,
    maxResumes: Infinity,
    maxLetters: Infinity,
    allTemplates: true,
    atsScoreChecker: true,
    linkedinOptimizer: true,
    jobAnalyzer: true,
    jobSearch: true,
    smartApply: true,
    companyInsights: true,
    aiApplication: true,
    aiInterviewPrep: true,
    salaryNegotiation: true,
    keywordMatching: true,
    versionHistory: true,
    analytics: true,
    customBranding: true,
    prioritySupport: true,
  },
};

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSubscription = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      // If no session or session error, set to free plan without showing error
      if (!session || sessionError) {
        setSubscription({
          subscribed: false,
          plan_type: 'free',
          product_id: null,
          subscription_end: null,
        });
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      // Handle auth errors gracefully (e.g., expired session)
      if (error) {
        // If it's an auth error, just set to free plan without showing toast
        const errorMessage = error.message || String(error);
        if (errorMessage.includes('Auth') || errorMessage.includes('session') || errorMessage.includes('401') || errorMessage.includes('403')) {
          console.log('Auth session invalid, setting to free plan');
          setSubscription({
            subscribed: false,
            plan_type: 'free',
            product_id: null,
            subscription_end: null,
          });
          return;
        }
        // For other errors, show toast
        throw error;
      }

      setSubscription(data as SubscriptionStatus);
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Only show toast for non-auth errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (!errorMessage.includes('Auth') && !errorMessage.includes('session')) {
        toast.error('Failed to check subscription status');
      }
      setSubscription({
        subscribed: false,
        plan_type: 'free',
        product_id: null,
        subscription_end: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const getFeatures = (): PlanFeatures => {
    if (!subscription) return PLAN_FEATURES.free;
    return PLAN_FEATURES[subscription.plan_type];
  };

  const hasFeature = (feature: keyof PlanFeatures): boolean => {
    const features = getFeatures();
    return features[feature] as boolean;
  };

  const canCreateDocument = async (type: 'resume' | 'letter'): Promise<boolean> => {
    const features = getFeatures();
    const maxAllowed = type === 'resume' ? features.maxResumes : features.maxLetters;
    
    if (maxAllowed === Infinity) return true;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const table = type === 'resume' ? 'resume_versions' : 'letters';
    const { count, error } = await supabase
      .from(table)
      .select('*', { count: 'exact', head: true })
      .eq('user_id', session.user.id);

    if (error) {
      console.error(`Error checking ${type} count:`, error);
      return false;
    }

    return (count || 0) < maxAllowed;
  };

  useEffect(() => {
    checkSubscription();

    // Set up auth state change listener
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          checkSubscription();
        } else if (event === 'SIGNED_OUT') {
          setSubscription({
            subscribed: false,
            plan_type: 'free',
            product_id: null,
            subscription_end: null,
          });
          setLoading(false);
        }
      }
    );

    // Refresh subscription every 5 minutes (only if user is authenticated)
    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        checkSubscription();
      }
    }, 5 * 60 * 1000);

    return () => {
      authSubscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  return {
    subscription,
    loading,
    features: getFeatures(),
    hasFeature,
    canCreateDocument,
    refreshSubscription: checkSubscription,
  };
};
